import { create } from 'zustand'

import { toast } from '@/shared/lib/toast'

import * as customerApi from '../api/customer.api'

const initialState = {
  customers: [],
  meta: null,
  isLoading: true,
  isSubmitting: false,
  error: null,
  filters: {
    page: 1,
    limit: 10,
    search: '',
  },
}

export const useCustomerStore = create((set, get) => ({
  ...initialState,

  clearError: () => set({ error: null }),

  setFilters: (partial) =>
    set((state) => ({
      filters: {
        ...state.filters,
        ...partial,
      },
    })),

  fetchCustomers: async (params = {}) => {
    const filters = {
      ...get().filters,
      ...params,
    }

    set({
      isLoading: true,
      error: null,
      filters,
    })

    try {
      const query = {
        page: filters.page,
        limit: filters.limit,
        search: filters.search || undefined,
        period: filters.period || undefined,
        salesPerson: filters.salesPerson || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      }

      const result = await customerApi.getCustomers(query)

      set({
        customers: result.data?.customers ?? [],
        meta: {
          total: result.data?.total ?? 0,
          page: result.data?.page ?? 1,
          limit: result.data?.limit ?? 10,
          totalPages: result.data?.totalPages ?? 1,
        },
        isLoading: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to fetch customers'

      set({
        isLoading: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  createCustomer: async (payload) => {
    if (get().isSubmitting) return null

    set({ isSubmitting: true, error: null })

    try {
      const result = await customerApi.createCustomer(payload)

      toast.success(result.message || 'Customer created successfully')

      await get().fetchCustomers({ page: 1 })

      set({
        isSubmitting: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to create customer'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  updateCustomer: async (id, payload) => {
    set({ isSubmitting: true, error: null })

    try {
      const result = await customerApi.updateCustomer(id, payload)

      toast.success(result.message || 'Customer updated successfully')

      await get().fetchCustomers()

      set({
        isSubmitting: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to update customer'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  deleteCustomer: async (id) => {
    set({ isSubmitting: true, error: null })

    try {
      const result = await customerApi.deleteCustomer(id)

      toast.success(result.message || 'Customer deleted successfully')

      await get().fetchCustomers()

      set({
        isSubmitting: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to delete customer'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },
}))
